import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, Save, ToggleLeft, ToggleRight, 
  MessageSquare, RefreshCw, Trash2, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReviewSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    google_review_url: '',
    min_products: 2,
    min_session_time: 90,
    min_products_opened: 4,
    show_after_waiter: true,
    texts: null
  });
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchFeedback();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/review-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/feedback?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFeedback(data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoadingFeedback(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/review-settings`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      alert('Configuración guardada');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-stone-400" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">Sistema de Reseñas</h1>
              <p className="text-sm text-stone-500">Configura la solicitud de reseñas en Google</p>
            </div>
          </div>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>

        {/* Main Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100 mb-6">
          <h2 className="font-medium text-stone-800 mb-4 flex items-center gap-2">
            <Star size={18} className="text-amber-500" />
            Configuración General
          </h2>

          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div>
                <p className="font-medium">Sistema de reseñas activo</p>
                <p className="text-xs text-stone-400">Muestra solicitudes de reseña a usuarios comprometidos</p>
              </div>
              <button onClick={() => updateSetting('enabled', !settings.enabled)}>
                {settings.enabled ? 
                  <ToggleRight size={32} className="text-green-600" /> : 
                  <ToggleLeft size={32} className="text-stone-400" />
                }
              </button>
            </div>

            {/* Google Review URL */}
            <div>
              <label className="block text-sm font-medium mb-2">URL de Google Reviews</label>
              <input
                type="text"
                value={settings.google_review_url}
                onChange={(e) => updateSetting('google_review_url', e.target.value)}
                className="w-full p-3 border rounded-lg text-sm"
                placeholder="https://search.google.com/local/writereview?placeid=..."
              />
              <p className="text-xs text-stone-400 mt-1">
                Puedes encontrar este enlace en Google My Business
              </p>
            </div>

            {/* Show after waiter */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
              <div>
                <p className="font-medium">Mostrar después de "Mostrar al camarero"</p>
                <p className="text-xs text-stone-400">Solicita reseña tras cerrar el modal del camarero</p>
              </div>
              <button onClick={() => updateSetting('show_after_waiter', !settings.show_after_waiter)}>
                {settings.show_after_waiter ? 
                  <ToggleRight size={32} className="text-green-600" /> : 
                  <ToggleLeft size={32} className="text-stone-400" />
                }
              </button>
            </div>
          </div>
        </div>

        {/* Engagement Criteria */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100 mb-6">
          <h2 className="font-medium text-stone-800 mb-4 flex items-center gap-2">
            <Clock size={18} className="text-blue-500" />
            Criterios de Usuario Comprometido
          </h2>
          <p className="text-sm text-stone-500 mb-4">
            Solo se mostrará la solicitud de reseña si el usuario cumple AL MENOS UNO de estos criterios:
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <label className="block text-sm font-medium mb-2">Productos añadidos</label>
              <input
                type="number"
                value={settings.min_products}
                onChange={(e) => updateSetting('min_products', parseInt(e.target.value) || 2)}
                className="w-full p-2 border rounded-lg text-center"
                min="1"
              />
              <p className="text-xs text-stone-400 mt-1 text-center">o más productos</p>
            </div>

            <div className="p-4 bg-stone-50 rounded-lg">
              <label className="block text-sm font-medium mb-2">Tiempo de sesión</label>
              <input
                type="number"
                value={settings.min_session_time}
                onChange={(e) => updateSetting('min_session_time', parseInt(e.target.value) || 90)}
                className="w-full p-2 border rounded-lg text-center"
                min="30"
              />
              <p className="text-xs text-stone-400 mt-1 text-center">o más segundos</p>
            </div>

            <div className="p-4 bg-stone-50 rounded-lg">
              <label className="block text-sm font-medium mb-2">Productos vistos</label>
              <input
                type="number"
                value={settings.min_products_opened}
                onChange={(e) => updateSetting('min_products_opened', parseInt(e.target.value) || 4)}
                className="w-full p-2 border rounded-lg text-center"
                min="2"
              />
              <p className="text-xs text-stone-400 mt-1 text-center">o más productos abiertos</p>
            </div>
          </div>
        </div>

        {/* Feedback Received */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-stone-800 flex items-center gap-2">
              <MessageSquare size={18} className="text-purple-500" />
              Feedback Recibido
            </h2>
            <button 
              onClick={fetchFeedback}
              className="p-2 hover:bg-stone-100 rounded-lg"
              disabled={loadingFeedback}
            >
              <RefreshCw size={16} className={loadingFeedback ? 'animate-spin' : ''} />
            </button>
          </div>

          {feedback.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-8">
              No hay feedback todavía. Aparecerá aquí cuando los usuarios seleccionen "Podemos mejorar".
            </p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {feedback.map((item, idx) => (
                <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-stone-700">{item.feedback}</p>
                    <span className="text-xs text-stone-400 whitespace-nowrap">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-0.5 bg-stone-200 rounded">
                      {item.language}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Impact Info */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="font-medium text-amber-800 mb-2">📈 Impacto Estimado</h3>
          <p className="text-sm text-amber-700">
            Un sistema de reseñas bien implementado puede generar un <strong>+25-40% más de reseñas mensuales</strong>, 
            mejorando tu reputación online, aumentando el tráfico orgánico y generando más reservas.
          </p>
        </div>
      </div>
    </div>
  );
}
